import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import type { LucideIcon } from "lucide-react";
import {
  Activity,
  BarChart3,
  Bot,
  Brain,
  Cpu,
  Key,
  LineChart,
  Webhook,
  FileText,
  BookOpen,
  Server,
  ClipboardCheck,
  Zap,
} from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import {
  ActionCard,
  ActionCardGrid,
  ActionCardSection,
} from "@/components/ui/action-card";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { isPlatformOrg, isSuperAdmin } from "@/lib/roles";

export const Route = createFileRoute("/portal/automation/")({
  component: AutomationDashboard,
});

interface Feature {
  title: string;
  description: string;
  icon: LucideIcon;
  href: string;
  iconColor: string;
  /** Only visible when platform org is selected (implies superadmin) */
  platformOrgOnly?: boolean;
}

// Org-scoped overview — shown to superadmins for whatever org is selected (NOT gated on the
// platform org, unlike the platform-config tiles). The dashboard resolves its data from the
// currently-selected organisation, so it's useful on any org, not just the platform org.
const overviewFeatures: Feature[] = [
  {
    title: "Dashboard",
    description: "AI agent activity, deployment overview, and ROI metrics.",
    icon: BarChart3,
    href: "/portal/automation/dashboard",
    iconColor: "bg-orange-500",
  },
  {
    title: "Agent Entitlements",
    description: "Control which agents this organisation sees on its dashboard.",
    icon: Bot,
    href: "/portal/automation/agent-entitlements",
    iconColor: "bg-violet-500",
  },
];

const coreFeatures: Feature[] = [
  {
    title: "AI Policies",
    description: "Configure AI engagement policies and automation rules.",
    icon: Brain,
    href: "/portal/automation/policies",
    iconColor: "bg-purple-500",
  },
  {
    title: "Workflows",
    description: "Manage and execute automation workflows.",
    icon: Activity,
    href: "/portal/automation/workflows",
    iconColor: "bg-blue-500",
  },
  {
    title: "Credentials",
    description: "Manage API keys and service credentials.",
    icon: Key,
    href: "/portal/automation/credentials",
    iconColor: "bg-red-500",
  },
  {
    title: "Webhooks",
    description: "Configure webhook integrations.",
    icon: Webhook,
    href: "/portal/automation/webhooks",
    iconColor: "bg-indigo-500",
  },
];

const toolsFeatures: Feature[] = [
  {
    title: "Skills Catalog",
    description: "View available agent skills synced from GitHub.",
    icon: BookOpen,
    href: "/portal/automation/skills",
    iconColor: "bg-amber-500",
  },
  {
    title: "Content Review",
    description: "Review and approve AI-generated content.",
    icon: ClipboardCheck,
    href: "/portal/automation/content-review",
    iconColor: "bg-pink-500",
  },
  {
    title: "Audit Logs",
    description: "View customer service and AI agent logs.",
    icon: FileText,
    href: "/portal/automation/audit",
    iconColor: "bg-slate-700",
  },
];

const platformFeatures: Feature[] = [
  {
    title: "LLM Configuration",
    description: "Configure language model providers and settings.",
    icon: Cpu,
    href: "/portal/automation/llm",
    iconColor: "bg-emerald-500",
    platformOrgOnly: true,
  },
  {
    title: "Monitoring",
    description: "System health and performance metrics.",
    icon: LineChart,
    href: "/portal/automation/monitoring",
    iconColor: "bg-cyan-500",
    platformOrgOnly: true,
  },
  {
    title: "MCP Servers",
    description: "Model Context Protocol servers for agent capabilities.",
    icon: Server,
    href: "/portal/automation/mcp",
    iconColor: "bg-teal-500",
    platformOrgOnly: true,
  },
];

function AutomationDashboard() {
  const { session } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const isUserSuperAdmin = isSuperAdmin(session);
  const isOnPlatformOrg = isPlatformOrg(selectedOrganisation);
  const showPlatformFeatures = isUserSuperAdmin && isOnPlatformOrg;

  return (
    <>
      <Helmet>
        <title>Automation & AI | Portal</title>
      </Helmet>

      <div className="space-y-6">
        <PageHeader
          title="Automation & AI"
          description="Manage AI policies, workflows, and integrations."
          icon={Zap}
          eyebrow="Platform"
          gradient
        />

        {isUserSuperAdmin && (
          <ActionCardSection title="Overview">
            <ActionCardGrid columns={3}>
              {overviewFeatures.map((feature) => (
                <ActionCard
                  key={feature.href}
                  title={feature.title}
                  description={feature.description}
                  icon={feature.icon}
                  href={feature.href}
                  iconColor={feature.iconColor}
                />
              ))}
            </ActionCardGrid>
          </ActionCardSection>
        )}

        <ActionCardSection title="Core Features">
          <ActionCardGrid columns={2}>
            {coreFeatures.map((feature) => (
              <ActionCard
                key={feature.href}
                title={feature.title}
                description={feature.description}
                icon={feature.icon}
                href={feature.href}
                iconColor={feature.iconColor}
              />
            ))}
          </ActionCardGrid>
        </ActionCardSection>

        <ActionCardSection title="Tools">
          <ActionCardGrid columns={3}>
            {toolsFeatures.map((feature) => (
              <ActionCard
                key={feature.href}
                title={feature.title}
                description={feature.description}
                icon={feature.icon}
                href={feature.href}
                iconColor={feature.iconColor}
              />
            ))}
          </ActionCardGrid>
        </ActionCardSection>

        {showPlatformFeatures && (
          <ActionCardSection title="Platform Administration">
            <ActionCardGrid columns={3}>
              {platformFeatures.map((feature) => (
                <ActionCard
                  key={feature.href}
                  title={feature.title}
                  description={feature.description}
                  icon={feature.icon}
                  href={feature.href}
                  iconColor={feature.iconColor}
                />
              ))}
            </ActionCardGrid>
          </ActionCardSection>
        )}
      </div>
    </>
  );
}
